<template>
  <v-form ref="ETABLISSEMENT_CAISSES" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="ETABLISSEMENT_CAISSES">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <etablissement-urssaf v-if="isDisplayed('URSSAF')"
                              ref="URSSAF"
                              :theme="theme"
                              :showMissing="showMissing"
                              v-model:value=urssaf
                              @status=statusUrssaf
                              :over-ride-placeholder=placeholderUrssaf
                              :required="isRequired('URSSAF')"
                              :contextId=etablissement.id
                              :readOnly="readOnly('URSSAF')"
                              :objType=objType
                              :fiche=fiche
                              :sectionName="sectionName">
        </etablissement-urssaf>
        <etablissement-fnas v-if="isDisplayed('FNAS')"
                            ref="FNAS"
                            :theme="theme"
                            :showMissing="showMissing"
                            v-model:value=fnas
                            @status=statusFnas
                            :over-ride-placeholder=placeholderFnas
                            :required="isRequired('FNAS')"
                            :contextId=etablissement.id
                            :readOnly="readOnly('FNAS')"
                            :objType=objType
                            :fiche=fiche
                            :sectionName="sectionName">
        </etablissement-fnas>
        <etablissement-conges-spec v-if="isDisplayed('CONGES_SPEC')"
                                   ref="CONGES_SPEC"
                                   :theme="theme"
                                   :showMissing="showMissing"
                                   v-model:value=congesSpec
                                   @status=statusCongesSpec
                                   :over-ride-placeholder=placeholderCongesSpec
                                   :required="isRequired('CONGES_SPEC')"
                                   :contextId=etablissement.id
                                   :readOnly="readOnly('CONGES_SPEC')"
                                   :objType=objType
                                   :fiche=fiche
                                   :sectionName="sectionName">
        </etablissement-conges-spec>
        <caisse-select-complementaire v-if="isDisplayed('RETRAITE_COMPLEMENTAIRE_ID')"
                                      ref="RETRAITE_COMPLEMENTAIRE_ID"
                                      :theme="theme"
                                      :showMissing="showMissing"
                                      v-model:value=retraiteComplementaireId
                                      :moduleId=moduleId
                                      libelle="'Retraite complémentaire'"
                                      newLabel=''
                                      selectText=""
                                      @status=statusRetraiteComplementaireId
                                      :requiredAttributs="requiredAttributs"
                                      :autoSelect=true
                                      :ressource=ressourceCaisseSelectComplementaire
                                      :filters=retraiteComplementaireIdFilter
                                      :toggleReset=resetRetraiteComplementaireId
                                      :required="isRequired('RETRAITE_COMPLEMENTAIRE_ID')"
                                      :contextId=etablissement.id
                                      :readOnly="readOnly('RETRAITE_COMPLEMENTAIRE_ID')"
                                      :objType=objType
                                      :fiche=fiche
                                      :sectionName="sectionName">
        </caisse-select-complementaire>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudEtablissement from '@/generated/cruds/CrudEtablissement';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudEtablissement,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.ETABLISSEMENT_CAISSES,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderUrssaf: null,
        placeholderFnas: null,
        placeholderCongesSpec: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceCaisseSelectComplementaire () { return RessourceGenControl(GenControl.ETABLISSEMENT_CAISSES_CAISSE_SELECT_COMPLEMENTAIRE); },

      ressource () { return RessourceGenSection(GenSection.ETABLISSEMENT_CAISSES); },

      fields (): string[] {
        return ['URSSAF', 'FNAS', 'CONGES_SPEC', 'RETRAITE_COMPLEMENTAIRE_ID'];
      },

      extraButtons (): Button[] {
        return [];
      },

      retraiteComplementaireIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editEtablissement.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("URSSAF", false);
          this.readOnlyMap.set("FNAS", false);
          this.readOnlyMap.set("CONGES_SPEC", false);
          this.readOnlyMap.set("RETRAITE_COMPLEMENTAIRE_ID", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("URSSAF", this.requiredMissing(requiredAttributs, "URSSAF") || !!this.fiche);
          this.displayMap.set("FNAS", this.requiredMissing(requiredAttributs, "FNAS") || !!this.fiche);
          this.displayMap.set("CONGES_SPEC", this.requiredMissing(requiredAttributs, "CONGES_SPEC") || !!this.fiche);
          this.displayMap.set("RETRAITE_COMPLEMENTAIRE_ID", this.requiredMissing(requiredAttributs, "RETRAITE_COMPLEMENTAIRE_ID") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("URSSAF", this.requiredMissing(requiredAttributs, "URSSAF") || !this.fiche);
          this.requiredMap.set("FNAS", this.requiredMissing(requiredAttributs, "FNAS") || !this.fiche);
          this.requiredMap.set("CONGES_SPEC", this.requiredMissing(requiredAttributs, "CONGES_SPEC") || !this.fiche);
          this.requiredMap.set("RETRAITE_COMPLEMENTAIRE_ID", this.requiredMissing(requiredAttributs, "RETRAITE_COMPLEMENTAIRE_ID") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoCaisses' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
